/**
 * Config Key Validation Utilities
 *
 * Provides allowlist-based validation for seed import configuration keys
 * to prevent prototype pollution attacks and other malicious config key injection.
 *
 * Security Context:
 * - Addresses security-review.md Finding #2 (HIGH severity)
 * - Prevents prototype pollution via __proto__, constructor, prototype keys
 * - Validates config keys before including in API payloads
 * - Defense-in-depth measure (backend must also validate)
 *
 * @module configValidation
 */

/**
 * Valid config keys allowlist for seed import settings
 *
 * Only keys in this array will be accepted in the settings object sent to the API.
 * Add new config keys here when implementing additional seed import options.
 *
 * Current valid keys:
 * - auto_approve: Set seeds to Active status immediately instead of Pending
 */
export const VALID_CONFIG_KEYS = ['auto_approve'];

/**
 * Validates a config key against the allowlist
 *
 * @param key - Config key to validate
 * @returns true if key is in the allowlist, false otherwise
 *
 * @example
 * ```typescript
 * isValidConfigKey('auto_approve')  // true
 * isValidConfigKey('__proto__')     // false
 * isValidConfigKey('unknown_key')   // false
 * ```
 */
export function isValidConfigKey(key: string): boolean {
  return VALID_CONFIG_KEYS.includes(key);
}

/**
 * Sanitizes a config object to only include allowlisted keys
 *
 * This function filters out any config keys not in the VALID_CONFIG_KEYS allowlist,
 * preventing malicious keys from being included in API payloads.
 *
 * Security Features:
 * - Blocks prototype pollution attempts (__proto__, constructor, prototype)
 * - Logs warnings for rejected keys (helps detect attacks in production logs)
 * - Returns new object (doesn't mutate input)
 * - Type-safe with TypeScript
 *
 * @param configs - Raw config object from React state (may contain malicious keys)
 * @returns Sanitized config object containing only allowlisted keys
 *
 * @example
 * ```typescript
 * // Valid config passes through
 * sanitizeConfigKeys({ auto_approve: true })
 * // => { auto_approve: true }
 *
 * // Invalid keys are filtered out
 * sanitizeConfigKeys({ auto_approve: true, __proto__: 'polluted' })
 * // => { auto_approve: true }
 * // Console: [Security] Invalid config key rejected: __proto__
 * ```
 */
export function sanitizeConfigKeys(
  configs: Record<string, boolean | string>
): Record<string, boolean | string> {
  const sanitized: Record<string, boolean | string> = {};

  Object.entries(configs).forEach(([key, value]) => {
    if (!isValidConfigKey(key)) {
      console.warn(`[Security] Invalid config key rejected: ${key}`);
      return;
    }
    sanitized[key] = value;
  });

  return sanitized;
}

/**
 * Converts validated configs to API settings format.
 *
 * Transforms the validated config object into the format expected by backend API:
 * - Converts boolean true to string "true"
 * - Includes non-empty strings
 * - Excludes boolean false values
 * - Excludes empty strings
 *
 * Security: Should only be called on sanitized configs (after sanitizeConfigKeys).
 *
 * @param validatedConfigs - Config object already passed through sanitizeConfigKeys
 * @returns Settings object formatted for API payload
 *
 * @example
 * ```typescript
 * const sanitized = sanitizeConfigKeys({ auto_approve: true, invalid: 'key' });
 * const settings = convertConfigsToSettings(sanitized);
 * // Result: { auto_approve: 'true' }
 * ```
 */
export function convertConfigsToSettings(
  validatedConfigs: Record<string, boolean | string>
): Record<string, string> {
  const settings: Record<string, string> = {};

  Object.entries(validatedConfigs).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      // Only include boolean true (backend expects string "true")
      if (value === true) {
        settings[key] = 'true';
      }
    } else if (value && typeof value === 'string') {
      // Include non-empty strings
      settings[key] = value;
    }
  });

  return settings;
}
